/**
 * Admin API helper — fetches per-user monthly usage from GET /admin/usage.
 * Only usable by Gary's account (admin sub guard enforced server-side too).
 */
import { fetchWithAuth } from './api';

export interface OperationBreakdown {
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  units: number;
  costUsd: number;
  lastModel?: string;
}

export type OperationType =
  | 'interview_turn'
  | 'session_title'
  | 'book_generation'
  | 'book_outline'
  | 'book_ordering'
  | 'outline_refine'
  | 'book_section_generation'
  | 'session_classification'
  | 'session_summary'
  | 'ai_edit_rewrite'
  | 'document_extract'
  | 'voice_session'
  | 'voice_minutes'
  | 'audio_upload'
  | 'photo_upload'
  | 'pdf_export'
  | 'cover_pdf_export'
  | 'toc_generation'
  | 'index_generation'
  | 'cover_design'
  | 'job_failed'
  | 'cartesia_tts'
  | 'polly_tts'
  | 'transcribe_stt';

export interface UserUsageRow {
  sub: string;
  fullSub?: string;
  displayName?: string;
  month: string;
  totalCostUsd: number;
  breakdown: Partial<Record<OperationType, OperationBreakdown>>;
}

export interface UsageResponse {
  month: string;
  users: UserUsageRow[];
}

const REST_BASE = (import.meta.env.VITE_REST_API_URL as string ?? '').replace(/\/$/, '');

export async function fetchUsage(month: string): Promise<UsageResponse> {
  const url = `${REST_BASE}/admin/usage?month=${encodeURIComponent(month)}`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GET /admin/usage failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<UsageResponse>;
}

export interface CartesiaCreditsResponse {
  month: string;
  creditsUsed: number;
  startTs: string;
  endTs: string;
}

export async function fetchCartesiaCredits(): Promise<CartesiaCreditsResponse> {
  const url = `${REST_BASE}/admin/cartesia-credits`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GET /admin/cartesia-credits failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<CartesiaCreditsResponse>;
}

export interface BetaSignup {
  email: string;
  name: string;
  reason: string;
  platforms?: string[];
  status: 'pending' | 'approved' | 'auto_approved';
  createdAt: string;
  approvedAt: string | null;
}

export interface BetaApproveResult {
  approved: string[];
  errors: { email: string; error: string }[];
}

export async function fetchBetaSignups(): Promise<BetaSignup[]> {
  const url = `${REST_BASE}/admin/beta-signups`;
  const res = await fetchWithAuth(url);
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`GET /admin/beta-signups failed (${res.status}): ${text}`);
  }
  const data = await res.json() as { signups: BetaSignup[] };
  return data.signups;
}

export async function approveBetaUsers(emails: string[]): Promise<BetaApproveResult> {
  const url = `${REST_BASE}/admin/beta-approve`;
  const res = await fetchWithAuth(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ emails }),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`POST /admin/beta-approve failed (${res.status}): ${text}`);
  }
  return res.json() as Promise<BetaApproveResult>;
}
